import * as THREE from 'three';
import { DEFAULT_UNITS_PER_METER_WITHOUT_FLOORPLAN } from '../constants';
import { FAB_COLORS } from '../components/canvas/amhs/rendering/fabColors';

/** World units per foot (default 6 units/m × 0.3048 m/ft). */
export const WORLD_UNITS_PER_FOOT = DEFAULT_UNITS_PER_METER_WITHOUT_FLOORPLAN * 0.3048;

/** Large-format dark blue slab — 40×40 ft squares with thin grout (3D fab floor). */
export const CLEANROOM_FLOOR_TILE_WIDTH_FEET = 40;
export const CLEANROOM_FLOOR_TILE_DEPTH_FEET = 40;
/** @deprecated Use TILE_WIDTH_FEET / TILE_DEPTH_FEET — kept for callers expecting a single foot value. */
export const CLEANROOM_FLOOR_TILE_FEET = CLEANROOM_FLOOR_TILE_DEPTH_FEET;
export const CLEANROOM_FLOOR_TILE_WIDTH_WORLD =
  WORLD_UNITS_PER_FOOT * CLEANROOM_FLOOR_TILE_WIDTH_FEET;
export const CLEANROOM_FLOOR_TILE_DEPTH_WORLD =
  WORLD_UNITS_PER_FOOT * CLEANROOM_FLOOR_TILE_DEPTH_FEET;
/** @deprecated Use TILE_WIDTH_WORLD — alias for the tile long edge. */
export const CLEANROOM_FLOOR_TILE_WORLD = CLEANROOM_FLOOR_TILE_WIDTH_WORLD;
/** One large slab per texture repeat. */
export const CLEANROOM_FLOOR_MOSAIC_COLS = 1;
export const CLEANROOM_FLOOR_MOSAIC_ROWS = 1;
/** @deprecated Use MOSAIC_COLS / MOSAIC_ROWS. */
export const CLEANROOM_FLOOR_MOSAIC_CELLS = CLEANROOM_FLOOR_MOSAIC_COLS;
export const CLEANROOM_FLOOR_TILE_AQUAMARINE = FAB_COLORS.floorTileAquamarine;
export const CLEANROOM_FLOOR_TILE_GLASS_BLUE = FAB_COLORS.floorTileGlassBlue;
export const CLEANROOM_FLOOR_TILE_PISTA_GREEN = FAB_COLORS.floorTilePistaGreen;
export const CLEANROOM_FLOOR_GROUT = FAB_COLORS.floorGrout;
/** Slabs packed per texture repeat. */
export const CLEANROOM_FLOOR_TILES_PER_REPEAT = CLEANROOM_FLOOR_MOSAIC_COLS * CLEANROOM_FLOOR_MOSAIC_ROWS;

/** Base tint for 3D fab floor (gray cleanroom floor). */
export const FAB_3D_FLOOR_TILE_COLOR = '#94A3B8';

/** 2×2 marble tile albedo in public/textures (tinted with {@link FAB_3D_FLOOR_TILE_COLOR}). */
export const FAB_FLOOR_TILE_TEXTURE_URL = '/textures/fab-floor-tiles.png';
/** Multiplier on UV repeat when using the photo floor (smaller on-screen tiles). */
export const FAB_FLOOR_PHOTO_TILE_REPEAT_SCALE = 6;

/** 3D fab floor — one texture repeat = 2×2 cells (40×40 ft macro). */
export const WHITE_GLASS_FLOOR_TILE_WIDTH_FEET = CLEANROOM_FLOOR_TILE_WIDTH_FEET / 2;
export const WHITE_GLASS_FLOOR_TILE_DEPTH_FEET = CLEANROOM_FLOOR_TILE_DEPTH_FEET / 2;
export const WHITE_GLASS_FLOOR_MOSAIC_COLS = 2;
export const WHITE_GLASS_FLOOR_MOSAIC_ROWS = 2;
/** Light gray cleanroom grout / grid lines between tiles. */
export const WHITE_GLASS_FLOOR_GROUT = '#B8C4CE';
export const WHITE_GLASS_FLOOR_LINE_OPACITY = 0.65;
export const WHITE_GLASS_FLOOR_TILE_WIDTH_WORLD =
  WORLD_UNITS_PER_FOOT * WHITE_GLASS_FLOOR_TILE_WIDTH_FEET;
export const WHITE_GLASS_FLOOR_TILE_DEPTH_WORLD =
  WORLD_UNITS_PER_FOOT * WHITE_GLASS_FLOOR_TILE_DEPTH_FEET;
/** Glossy polished marble epoxy floor. */
export const CLEANROOM_FLOOR_COLOR = FAB_COLORS.floor;
export const CLEANROOM_FLOOR_COLOR_DARK = '#243848';
export const CLEANROOM_FLOOR_SEAM = FAB_COLORS.floorSeam;
export const CLEANROOM_FLOOR_MARKING = FAB_COLORS.floorLane;
export const GRILLE_FRAME_COLOR = FAB_COLORS.grilleFrame;
export const GRILLE_SURFACE_COLOR = FAB_COLORS.grillePanel;
export const GRILLE_RECESS_COLOR = FAB_COLORS.grilleInterior;
export const GRILLE_HOLE_COLOR = FAB_COLORS.grilleHole;

export type FloorSegment = { cx: number; cz: number; sx: number; sy: number; sz: number };
export type GrilleStrip = { cx: number; cz: number; length: number; width: number };
export type CleanroomFloorDetail = 'high' | 'medium' | 'low';
export type CleanroomFloorLayout = {
  seams: FloorSegment[];
  markings: FloorSegment[];
  grilleStrips: GrilleStrip[];
  grilleFrames: FloorSegment[];
  grilleRecesses: FloorSegment[];
  grilleSurfaces: FloorSegment[];
  grilleHoles: FloorSegment[];
};

export function floorDecorScale(width: number, depth: number) {
  const span = Math.min(width, depth);
  return {
    span,
    panelSize: THREE.MathUtils.clamp(span * 0.22, 140, 880),
    seamW: span * 0.00024,
    seamH: span * 0.00006,
    lineW: span * 0.00085,
    lineH: span * 0.00008,
    grilleSpacing: span * 0.135,
    grilleWidth: span * 0.022,
    grilleLenRatio: 0.76,
    inset: span * 0.05,
    holePitch: span * 0.00175,
    holeD: span * 0.001,
    frameT: span * 0.0004,
    recessH: span * 0.00045,
    borderT: span * 0.00028,
    grilleTileWorld: span * 0.028,
  };
}

let floorMaterial: THREE.MeshPhysicalMaterial | null = null;
let floorMaterialDark: THREE.MeshPhysicalMaterial | null = null;
let seamMaterial: THREE.MeshStandardMaterial | null = null;
let markingMaterial: THREE.MeshPhysicalMaterial | null = null;
let grilleFrameMaterial: THREE.MeshStandardMaterial | null = null;
let grilleSurfaceMaterial: THREE.MeshStandardMaterial | null = null;
let grilleRecessMaterial: THREE.MeshStandardMaterial | null = null;
let grilleHoleMaterial: THREE.MeshStandardMaterial | null = null;
let unitBoxGeometry: THREE.BoxGeometry | null = null;
let holeCylinderGeometry: THREE.CylinderGeometry | null = null;
let grillePerforatedTexture: THREE.CanvasTexture | null = null;
let shineTileMap: THREE.CanvasTexture | null = null;
let shineTileRoughness: THREE.CanvasTexture | null = null;
let shineTileNormal: THREE.CanvasTexture | null = null;
let shineTileCacheVersion = 0;
const SHINE_TILE_TEXTURE_VERSION = 18;
let whiteGlassTileMap: THREE.CanvasTexture | null = null;
let whiteGlassTileRoughness: THREE.CanvasTexture | null = null;
let whiteGlassTileNormal: THREE.CanvasTexture | null = null;
let whiteGlassTileCacheVersion = 0;
const WHITE_GLASS_TILE_TEXTURE_VERSION = 10;

let matteFloorRoughnessMap: THREE.CanvasTexture | null = null;
let fabFloorPhotoAlbedo: THREE.Texture | null = null;
let fabFloorGrainRoughness: THREE.Texture | null = null;
let fabFloorGrainBump: THREE.Texture | null = null;
let fabFloorPhotoLoadPromise: Promise<THREE.Texture | null> | null = null;

function configureFabFloorGrainTexture(map: THREE.Texture): void {
  map.wrapS = map.wrapT = THREE.RepeatWrapping;
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 8;
  map.needsUpdate = true;
}

function bindFabFloorGrainMaps(source: THREE.Texture): void {
  fabFloorPhotoAlbedo = source;
  fabFloorGrainRoughness = source.clone();
  fabFloorGrainRoughness.colorSpace = THREE.NoColorSpace;
  fabFloorGrainBump = source.clone();
  fabFloorGrainBump.colorSpace = THREE.NoColorSpace;
}

/** Tileable salt-and-pepper grain when the PNG is unavailable. */
function buildFabFloorGranuleGrainCanvasTexture(): THREE.CanvasTexture {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    const fallback = new THREE.CanvasTexture(canvas);
    configureFabFloorGrainTexture(fallback);
    return fallback;
  }
  const img = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const fine = hash2(x * 0.91, y * 0.87);
      const med = hash2(Math.floor(x / 4) * 1.7, Math.floor(y / 4) * 1.9);
      const coarse = hash2(Math.floor(x / 16) * 0.6, Math.floor(y / 16) * 0.55);
      const n = fine * 0.48 + med * 0.34 + coarse * 0.18;
      const g = Math.floor(Math.min(255, Math.max(0, n * 255)));
      const i = (y * size + x) * 4;
      img.data[i] = g;
      img.data[i + 1] = g;
      img.data[i + 2] = g;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const map = new THREE.CanvasTexture(canvas);
  configureFabFloorGrainTexture(map);
  return map;
}

/** Uniform high roughness for photo tile albedo (matte stone). */
export function getMatteFloorRoughnessTexture(): THREE.CanvasTexture {
  if (!matteFloorRoughnessMap) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 4;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#e0e0e0';
      ctx.fillRect(0, 0, 4, 4);
    }
    matteFloorRoughnessMap = new THREE.CanvasTexture(canvas);
    matteFloorRoughnessMap.wrapS = matteFloorRoughnessMap.wrapT = THREE.RepeatWrapping;
    matteFloorRoughnessMap.needsUpdate = true;
  }
  return matteFloorRoughnessMap;
}

/** Load granular fab floor albedo once; procedural grain if the PNG is missing. */
export function loadFabFloorPhotoAlbedo(): Promise<THREE.Texture | null> {
  if (fabFloorPhotoAlbedo) return Promise.resolve(fabFloorPhotoAlbedo);
  if (fabFloorPhotoLoadPromise) return fabFloorPhotoLoadPromise;
  fabFloorPhotoLoadPromise = new Promise((resolve) => {
    const loader = new THREE.TextureLoader();
    loader.load(
      FAB_FLOOR_TILE_TEXTURE_URL,
      (map) => {
        configureFabFloorGrainTexture(map);
        bindFabFloorGrainMaps(map);
        resolve(map);
      },
      undefined,
      () => {
        const proc = buildFabFloorGranuleGrainCanvasTexture();
        bindFabFloorGrainMaps(proc);
        resolve(proc);
      }
    );
  });
  return fabFloorPhotoLoadPromise;
}

export function getFabFloorPhotoAlbedo(): THREE.Texture | null {
  return fabFloorPhotoAlbedo;
}

export function getFabFloorGrainRoughnessMap(): THREE.Texture | null {
  return fabFloorGrainRoughness;
}

export function getFabFloorGrainBumpMap(): THREE.Texture | null {
  return fabFloorGrainBump;
}

function hash2(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

function lerpRgb(
  a: [number, number, number],
  b: [number, number, number],
  t: number
): [number, number, number] {
  const u = THREE.MathUtils.clamp(t, 0, 1);
  return [
    Math.round(a[0] + (b[0] - a[0]) * u),
    Math.round(a[1] + (b[1] - a[1]) * u),
    Math.round(a[2] + (b[2] - a[2]) * u),
  ];
}

/** Large flowing swirl field — tuned for 20×20 ft slabs. */
function marbleField(x: number, y: number, seed: number): number {
  const n1 = hash2(x * 0.42 + seed * 4.1, y * 0.38 + seed * 2.3);
  const n2 = hash2(x * 0.88 + seed * 1.7, y * 0.82 + seed * 3.4);
  const swirl =
    Math.sin(x * 1.05 + seed) * Math.cos(y * 0.95 + seed * 0.7) +
    Math.sin(x * 2.2 - y * 1.9 + seed * 1.2) * 0.48;
  return THREE.MathUtils.clamp(n1 * 0.34 + n2 * 0.26 + swirl * 0.4, 0, 1);
}

type MarbleSample = {
  rgb: [number, number, number];
  roughnessByte: number;
  height: number;
};

/** Glossy dark blue glass tile — marbled navy base with lighter blue veins. */
function sampleMarbleTile(lx: number, ly: number, seed: number): MarbleSample {
  const x = lx * 4.2 + seed * 0.42;
  const y = ly * 4.2 + seed * 0.31;
  const field = marbleField(x, y, seed);
  const vein1 = Math.sin(x * 1.65 + y * 1.15 + field * 2.8 + seed * 1.6);
  const vein2 = Math.sin(x * 2.35 - y * 2.05 + seed * 1.1) * 0.62;
  const veinMag = Math.abs(vein1 + vein2);
  const speck = hash2(x * 11, y * 9 + seed);

  const navyDeep: [number, number, number] = [30, 52, 72];
  const darkBlue: [number, number, number] = [52, 88, 118];
  const midBlue: [number, number, number] = [74, 112, 142];
  const slateBlue: [number, number, number] = [98, 138, 168];
  const glassShine: [number, number, number] = [128, 168, 198];

  let rgb: [number, number, number];
  let roughnessByte: number;

  if (veinMag > 0.78) {
    rgb = lerpRgb(slateBlue, midBlue, (veinMag - 0.78) / 0.22);
    roughnessByte = 0x22;
  } else if (veinMag > 0.5) {
    rgb = lerpRgb(midBlue, darkBlue, (veinMag - 0.5) / 0.28);
    roughnessByte = 0x1c;
  } else if (field > 0.86 && speck > 0.91) {
    rgb = lerpRgb(glassShine, slateBlue, speck);
    roughnessByte = 0x12;
  } else if (field < 0.22) {
    rgb = lerpRgb(navyDeep, darkBlue, field / 0.22);
    roughnessByte = 0x28;
  } else if (field > 0.72) {
    rgb = lerpRgb(darkBlue, midBlue, (field - 0.72) / 0.28);
    roughnessByte = 0x18;
  } else {
    rgb = lerpRgb(navyDeep, darkBlue, (field - 0.2) / 0.52);
    roughnessByte = 0x20;
  }

  const heightField = marbleField(x * 0.9, y * 0.9, seed);
  const veinRelief = 1 - Math.abs(Math.sin(x * 1.9 + y * 1.6 + heightField * 2.4));
  const height =
    heightField * 0.28 +
    veinRelief * 0.24 +
    (hash2(x * 12, y * 12 + seed) - 0.5) * 0.05;

  return { rgb, roughnessByte, height };
}

/** Light cleanroom square tile — subtle anti-static flecks, clean light stone. */
function sampleWhiteGlassTile(lx: number, ly: number, seed: number): MarbleSample {
  const x = lx * 4.6 + seed * 0.41;
  const y = ly * 4.6 + seed * 0.33;
  const field = marbleField(x, y, seed);
  const speck = hash2(x * 19.2 + seed, y * 17.4);
  const fleck = hash2(x * 27.5, y * 23.8 + seed * 1.4);
  const grain = hash2(x * 44, y * 41 + seed) - 0.5;

  /** Cleanroom square tile palette around {@link FAB_3D_FLOOR_TILE_COLOR} (#ffffff). */
  const darkStone: [number, number, number] = [248, 248, 248];
  const midStone: [number, number, number] = [252, 252, 252];
  const lightStone: [number, number, number] = [255, 255, 255];
  const darkFleck: [number, number, number] = [242, 242, 242];

  let rgb = lerpRgb(darkStone, midStone, field * 0.58 + grain * 0.08);
  if (speck > 0.78) {
    rgb = lerpRgb(rgb, lightStone, ((speck - 0.78) / 0.22) * 0.42);
  }
  if (fleck > 0.86) {
    rgb = lerpRgb(rgb, darkFleck, ((fleck - 0.86) / 0.14) * 0.38);
  }

  const roughnessByte =
    speck > 0.92 ? 0x7e : field < 0.22 ? 0x82 : field > 0.78 ? 0x78 : 0x7c;

  const heightField = marbleField(x * 1.15, y * 1.15, seed);
  const height = heightField * 0.022 + grain * 0.01;

  return { rgb, roughnessByte, height };
}

function isGroutPixel(
  col: number,
  row: number,
  cols: number,
  rows: number,
  sizeW: number,
  sizeH: number,
  groutPx: number
): boolean {
  const cellW = sizeW / cols;
  const cellH = sizeH / rows;
  const lx = col - Math.floor(col / cellW) * cellW;
  const ly = row - Math.floor(row / cellH) * cellH;
  return lx < groutPx || ly < groutPx;
}

/** Polished dark blue glass slabs + thin black grout + normal/roughness maps. */
export function getCleanroomShineTileTextures(): {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  normalMap: THREE.CanvasTexture;
} {
  if (
    shineTileMap &&
    shineTileRoughness &&
    shineTileNormal &&
    shineTileCacheVersion === SHINE_TILE_TEXTURE_VERSION
  ) {
    return { map: shineTileMap, roughnessMap: shineTileRoughness, normalMap: shineTileNormal };
  }

  const cols = CLEANROOM_FLOOR_MOSAIC_COLS;
  const rows = CLEANROOM_FLOOR_MOSAIC_ROWS;
  const groutPx = 2;
  const slabAspect =
    (CLEANROOM_FLOOR_TILE_WIDTH_FEET * cols) / (CLEANROOM_FLOOR_TILE_DEPTH_FEET * rows);
  const sizeW = 512;
  const sizeH = Math.max(128, Math.round(sizeW / slabAspect));
  const cellW = sizeW / cols;
  const cellH = sizeH / rows;

  const mapCanvas = document.createElement('canvas');
  mapCanvas.width = sizeW;
  mapCanvas.height = sizeH;
  const mapCtx = mapCanvas.getContext('2d')!;

  const roughCanvas = document.createElement('canvas');
  roughCanvas.width = sizeW;
  roughCanvas.height = sizeH;
  const roughCtx = roughCanvas.getContext('2d')!;

  const normalCanvas = document.createElement('canvas');
  normalCanvas.width = sizeW;
  normalCanvas.height = sizeH;
  const normalCtx = normalCanvas.getContext('2d')!;

  const heights = new Float32Array(sizeW * sizeH);

  mapCtx.fillStyle = CLEANROOM_FLOOR_GROUT;
  mapCtx.fillRect(0, 0, sizeW, sizeH);
  roughCtx.fillStyle = '#808080';
  roughCtx.fillRect(0, 0, sizeW, sizeH);

  for (let row = 0; row < sizeH; row++) {
    for (let col = 0; col < sizeW; col++) {
      const cellCol = Math.floor(col / cellW);
      const cellRow = Math.floor(row / cellH);
      const seed = cellRow * 17 + cellCol * 31 + 0.5;
      const idx = row * sizeW + col;

      if (isGroutPixel(col, row, cols, rows, sizeW, sizeH, groutPx)) {
        heights[idx] = -0.2;
        mapCtx.fillStyle = CLEANROOM_FLOOR_GROUT;
        mapCtx.fillRect(col, row, 1, 1);
        roughCtx.fillStyle = '#909090';
        roughCtx.fillRect(col, row, 1, 1);
        continue;
      }

      const x0 = cellCol * cellW + groutPx;
      const y0 = cellRow * cellH + groutPx;
      const cw = cellW - groutPx * 2;
      const ch = cellH - groutPx * 2;
      const lx = (col - x0) / cw;
      const ly = (row - y0) / ch;
      const sample = sampleMarbleTile(lx, ly, seed);
      heights[idx] = sample.height;

      const [r, g, b] = sample.rgb;
      mapCtx.fillStyle = `rgb(${r},${g},${b})`;
      mapCtx.fillRect(col, row, 1, 1);

      roughCtx.fillStyle = `rgb(${sample.roughnessByte},${sample.roughnessByte},${sample.roughnessByte})`;
      roughCtx.fillRect(col, row, 1, 1);
    }
  }

  const normalStrength = 1.35;
  for (let row = 0; row < sizeH; row++) {
    for (let col = 0; col < sizeW; col++) {
      const idx = row * sizeW + col;
      if (isGroutPixel(col, row, cols, rows, sizeW, sizeH, groutPx)) {
        normalCtx.fillStyle = 'rgb(128,128,255)';
        normalCtx.fillRect(col, row, 1, 1);
        continue;
      }
      const hL = heights[idx - 1] ?? heights[idx];
      const hR = heights[idx + 1] ?? heights[idx];
      const hD = heights[idx - sizeW] ?? heights[idx];
      const hU = heights[idx + sizeW] ?? heights[idx];
      let nx = (hL - hR) * normalStrength;
      let ny = (hD - hU) * normalStrength;
      let nz = 1;
      const len = Math.hypot(nx, ny, nz) || 1;
      nx /= len;
      ny /= len;
      nz /= len;
      const nr = Math.round((nx * 0.5 + 0.5) * 255);
      const ng = Math.round((ny * 0.5 + 0.5) * 255);
      const nb = Math.round((nz * 0.5 + 0.5) * 255);
      normalCtx.fillStyle = `rgb(${nr},${ng},${nb})`;
      normalCtx.fillRect(col, row, 1, 1);
    }
  }

  const map = new THREE.CanvasTexture(mapCanvas);
  map.wrapS = map.wrapT = THREE.RepeatWrapping;
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 8;
  map.needsUpdate = true;

  const roughnessMap = new THREE.CanvasTexture(roughCanvas);
  roughnessMap.wrapS = roughnessMap.wrapT = THREE.RepeatWrapping;
  roughnessMap.anisotropy = 4;
  roughnessMap.needsUpdate = true;

  const normalMap = new THREE.CanvasTexture(normalCanvas);
  normalMap.wrapS = normalMap.wrapT = THREE.RepeatWrapping;
  normalMap.anisotropy = 4;
  normalMap.needsUpdate = true;

  shineTileMap = map;
  shineTileRoughness = roughnessMap;
  shineTileNormal = normalMap;
  shineTileCacheVersion = SHINE_TILE_TEXTURE_VERSION;
  return { map, roughnessMap, normalMap };
}

type BuildTileTextureOpts = {
  groutPx?: number;
  groutRoughnessByte?: number;
};

function buildTileTextureSet(
  cols: number,
  rows: number,
  groutColor: string,
  sampleTile: (lx: number, ly: number, seed: number) => MarbleSample,
  opts: BuildTileTextureOpts = {}
): {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  normalMap: THREE.CanvasTexture;
} {
  const groutPx = opts.groutPx ?? 2;
  const groutRoughnessByte = opts.groutRoughnessByte ?? 0x90;
  const slabAspect =
    (WHITE_GLASS_FLOOR_TILE_WIDTH_FEET * cols) / (WHITE_GLASS_FLOOR_TILE_DEPTH_FEET * rows);
  const sizeW = 512;
  const sizeH = Math.max(128, Math.round(sizeW / slabAspect));
  const cellW = sizeW / cols;
  const cellH = sizeH / rows;

  const mapCanvas = document.createElement('canvas');
  mapCanvas.width = sizeW;
  mapCanvas.height = sizeH;
  const mapCtx = mapCanvas.getContext('2d')!;

  const roughCanvas = document.createElement('canvas');
  roughCanvas.width = sizeW;
  roughCanvas.height = sizeH;
  const roughCtx = roughCanvas.getContext('2d')!;

  const normalCanvas = document.createElement('canvas');
  normalCanvas.width = sizeW;
  normalCanvas.height = sizeH;
  const normalCtx = normalCanvas.getContext('2d')!;

  const heights = new Float32Array(sizeW * sizeH);

  mapCtx.fillStyle = groutColor;
  mapCtx.fillRect(0, 0, sizeW, sizeH);
  roughCtx.fillStyle = '#808080';
  roughCtx.fillRect(0, 0, sizeW, sizeH);

  for (let row = 0; row < sizeH; row++) {
    for (let col = 0; col < sizeW; col++) {
      const cellCol = Math.floor(col / cellW);
      const cellRow = Math.floor(row / cellH);
      const seed = cellRow * 17 + cellCol * 31 + 0.5;
      const idx = row * sizeW + col;

      if (isGroutPixel(col, row, cols, rows, sizeW, sizeH, groutPx)) {
        heights[idx] = -0.32;
        mapCtx.fillStyle = groutColor;
        mapCtx.fillRect(col, row, 1, 1);
        roughCtx.fillStyle = `rgb(${groutRoughnessByte},${groutRoughnessByte},${groutRoughnessByte})`;
        roughCtx.fillRect(col, row, 1, 1);
        continue;
      }

      const x0 = cellCol * cellW + groutPx;
      const y0 = cellRow * cellH + groutPx;
      const cw = cellW - groutPx * 2;
      const ch = cellH - groutPx * 2;
      const lx = (col - x0) / cw;
      const ly = (row - y0) / ch;
      const sample = sampleTile(lx, ly, seed);
      heights[idx] = sample.height;

      const [r, g, b] = sample.rgb;
      mapCtx.fillStyle = `rgb(${r},${g},${b})`;
      mapCtx.fillRect(col, row, 1, 1);

      roughCtx.fillStyle = `rgb(${sample.roughnessByte},${sample.roughnessByte},${sample.roughnessByte})`;
      roughCtx.fillRect(col, row, 1, 1);
    }
  }

  const normalStrength = 1.15;
  for (let row = 0; row < sizeH; row++) {
    for (let col = 0; col < sizeW; col++) {
      const idx = row * sizeW + col;
      if (isGroutPixel(col, row, cols, rows, sizeW, sizeH, groutPx)) {
        normalCtx.fillStyle = 'rgb(128,128,255)';
        normalCtx.fillRect(col, row, 1, 1);
        continue;
      }
      const hL = heights[idx - 1] ?? heights[idx];
      const hR = heights[idx + 1] ?? heights[idx];
      const hD = heights[idx - sizeW] ?? heights[idx];
      const hU = heights[idx + sizeW] ?? heights[idx];
      let nx = (hL - hR) * normalStrength;
      let ny = (hD - hU) * normalStrength;
      let nz = 1;
      const len = Math.hypot(nx, ny, nz) || 1;
      nx /= len;
      ny /= len;
      nz /= len;
      const nr = Math.round((nx * 0.5 + 0.5) * 255);
      const ng = Math.round((ny * 0.5 + 0.5) * 255);
      const nb = Math.round((nz * 0.5 + 0.5) * 255);
      normalCtx.fillStyle = `rgb(${nr},${ng},${nb})`;
      normalCtx.fillRect(col, row, 1, 1);
    }
  }

  const map = new THREE.CanvasTexture(mapCanvas);
  map.wrapS = map.wrapT = THREE.RepeatWrapping;
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 8;
  map.needsUpdate = true;

  const roughnessMap = new THREE.CanvasTexture(roughCanvas);
  roughnessMap.wrapS = roughnessMap.wrapT = THREE.RepeatWrapping;
  roughnessMap.anisotropy = 4;
  roughnessMap.needsUpdate = true;

  const normalMap = new THREE.CanvasTexture(normalCanvas);
  normalMap.wrapS = normalMap.wrapT = THREE.RepeatWrapping;
  normalMap.anisotropy = 4;
  normalMap.needsUpdate = true;

  return { map, roughnessMap, normalMap };
}

/** World-space white grid line positions for the glass fab floor (local XY before -X rotation). */
export function buildWhiteGlassFloorGridPositions(
  width: number,
  depth: number,
  tileWorld = WHITE_GLASS_FLOOR_TILE_WIDTH_WORLD
): Float32Array {
  const halfW = width / 2;
  const halfD = depth / 2;
  const positions: number[] = [];
  const y = 0.018;

  for (let x = -halfW; x <= halfW + tileWorld * 0.001; x += tileWorld) {
    positions.push(x, -halfD, y, x, halfD, y);
  }
  for (let z = -halfD; z <= halfD + tileWorld * 0.001; z += tileWorld) {
    positions.push(-halfW, z, y, halfW, z, y);
  }

  return new Float32Array(positions);
}

/** Procedural fallback when the fab floor image is unavailable. */
export function getCleanroomWhiteGlassTileTexturesProcedural(): {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  normalMap: THREE.CanvasTexture;
} {
  if (
    whiteGlassTileMap &&
    whiteGlassTileRoughness &&
    whiteGlassTileNormal &&
    whiteGlassTileCacheVersion === WHITE_GLASS_TILE_TEXTURE_VERSION
  ) {
    return {
      map: whiteGlassTileMap,
      roughnessMap: whiteGlassTileRoughness,
      normalMap: whiteGlassTileNormal,
    };
  }

  const { map, roughnessMap, normalMap } = buildTileTextureSet(
    WHITE_GLASS_FLOOR_MOSAIC_COLS,
    WHITE_GLASS_FLOOR_MOSAIC_ROWS,
    WHITE_GLASS_FLOOR_GROUT,
    sampleWhiteGlassTile,
    { groutPx: 3, groutRoughnessByte: 0x78 }
  );

  whiteGlassTileMap = map;
  whiteGlassTileRoughness = roughnessMap;
  whiteGlassTileNormal = normalMap;
  whiteGlassTileCacheVersion = WHITE_GLASS_TILE_TEXTURE_VERSION;
  return { map, roughnessMap, normalMap };
}

/** @deprecated Use image loader + {@link getCleanroomWhiteGlassTileTexturesProcedural} fallback. */
export function getCleanroomWhiteGlassTileTextures(): ReturnType<
  typeof getCleanroomWhiteGlassTileTexturesProcedural
> {
  return getCleanroomWhiteGlassTileTexturesProcedural();
}

export function whiteGlassTileRepeatForFloor(width: number, depth: number): { x: number; y: number } {
  const macroW = WHITE_GLASS_FLOOR_TILE_WIDTH_WORLD * WHITE_GLASS_FLOOR_MOSAIC_COLS;
  const macroD = WHITE_GLASS_FLOOR_TILE_DEPTH_WORLD * WHITE_GLASS_FLOOR_MOSAIC_ROWS;
  return {
    x: Math.max(1, width / macroW),
    y: Math.max(1, depth / macroD),
  };
}

export function cleanroomTileRepeatForFloor(width: number, depth: number): { x: number; y: number } {
  const macroW = CLEANROOM_FLOOR_TILE_WIDTH_WORLD * CLEANROOM_FLOOR_MOSAIC_COLS;
  const macroD = CLEANROOM_FLOOR_TILE_DEPTH_WORLD * CLEANROOM_FLOOR_MOSAIC_ROWS;
  return {
    x: Math.max(1, width / macroW),
    y: Math.max(1, depth / macroD),
  };
}

export function configureGrilleTextureSampling(
  texture: THREE.CanvasTexture,
  anisotropy = 8
): THREE.CanvasTexture {
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = anisotropy;
  texture.needsUpdate = true;
  return texture;
}

export function getGrillePerforatedTexture(anisotropy = 8): THREE.CanvasTexture {
  if (!grillePerforatedTexture) {
    const tile = 512;
    const canvas = document.createElement('canvas');
    canvas.width = tile;
    canvas.height = tile;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = GRILLE_SURFACE_COLOR;
    ctx.fillRect(0, 0, tile, tile);
    const cols = 16;
    const rows = 8;
    const cellW = tile / cols;
    const cellH = tile / rows;
    const holeR = Math.min(cellW, cellH) * 0.19;
    ctx.imageSmoothingEnabled = true;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = (c + 0.5) * cellW;
        const y = (r + 0.5) * cellH;
        ctx.beginPath();
        ctx.arc(x, y, holeR * 1.06, 0, Math.PI * 2);
        ctx.fillStyle = '#4A5258';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, holeR * 0.88, 0, Math.PI * 2);
        ctx.fillStyle = GRILLE_HOLE_COLOR;
        ctx.fill();
      }
    }
    grillePerforatedTexture = new THREE.CanvasTexture(canvas);
    grillePerforatedTexture.wrapS = THREE.RepeatWrapping;
    grillePerforatedTexture.wrapT = THREE.RepeatWrapping;
    grillePerforatedTexture.colorSpace = THREE.SRGBColorSpace;
    configureGrilleTextureSampling(grillePerforatedTexture, anisotropy);
  }
  return grillePerforatedTexture;
}

export function createGrilleStripMaterial(
  strip: GrilleStrip,
  tileWorld: number,
  anisotropy = 8
): THREE.MeshStandardMaterial {
  const mat = getGrilleSurfaceMaterial().clone();
  const tex = getGrillePerforatedTexture(anisotropy).clone();
  const repeatX = Math.max(2, strip.length / tileWorld);
  const repeatY = Math.max(2, strip.width / tileWorld);
  tex.repeat.set(repeatX, repeatY);
  configureGrilleTextureSampling(tex, anisotropy);
  mat.map = tex;
  mat.color.set('#FFFFFF');
  mat.metalness = 0.52;
  mat.roughness = 0.38;
  mat.envMapIntensity = 0.55;
  return mat;
}

export function getCleanroomFloorUnitBox(): THREE.BoxGeometry {
  if (!unitBoxGeometry) unitBoxGeometry = new THREE.BoxGeometry(1, 1, 1);
  return unitBoxGeometry;
}

export function getCleanroomFloorHoleGeometry(): THREE.CylinderGeometry {
  if (!holeCylinderGeometry) holeCylinderGeometry = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
  return holeCylinderGeometry;
}

/** Fab layout tiles — fully diffuse; no IBL/specular “light pools” on the floor. */
export function applyMatteFabFloorTileMaterial(mat: THREE.MeshPhysicalMaterial): void {
  mat.metalness = 0;
  mat.roughness = 1;
  mat.clearcoat = 0;
  mat.clearcoatRoughness = 1;
  mat.envMapIntensity = 0;
  mat.reflectivity = 0;
  mat.specularIntensity = 0;
  mat.sheen = 0;
  mat.sheenRoughness = 1;
  mat.transmission = 0;
  mat.ior = 1;
  mat.bumpMap = null;
  mat.bumpScale = 0;
  mat.normalMap = null;
  mat.normalScale.set(1, 1);
  mat.needsUpdate = true;
}

function applyGlossyFloorProps(mat: THREE.MeshPhysicalMaterial): void {
  mat.color.set(CLEANROOM_FLOOR_COLOR);
  mat.metalness = 0;
  mat.roughness = 0.28;
  mat.clearcoat = 0.18;
  mat.clearcoatRoughness = 0.12;
  mat.envMapIntensity = 0.42;
  mat.reflectivity = 0.32;
  mat.ior = 1.52;
  mat.sheen = 0.12;
  mat.sheenRoughness = 0.22;
  mat.sheenColor = new THREE.Color(FAB_COLORS.floorHighlight);
  mat.needsUpdate = true;
}

export function getCleanroomFloorMaterial(darkMode: boolean): THREE.MeshPhysicalMaterial {
  if (darkMode) {
    if (!floorMaterialDark) {
      floorMaterialDark = new THREE.MeshPhysicalMaterial({
        color: CLEANROOM_FLOOR_COLOR_DARK,
        metalness: 0,
        roughness: 0.24,
        clearcoat: 0.18,
        clearcoatRoughness: 0.1,
        envMapIntensity: 0.62,
      });
    }
    return floorMaterialDark;
  }
  if (!floorMaterial) {
    floorMaterial = new THREE.MeshPhysicalMaterial({
      color: CLEANROOM_FLOOR_COLOR,
      metalness: 0,
      roughness: 0.28,
      clearcoat: 0.18,
      clearcoatRoughness: 0.12,
      envMapIntensity: 0.42,
      reflectivity: 0.32,
      ior: 1.52,
      sheen: 0.12,
      sheenRoughness: 0.22,
      sheenColor: new THREE.Color(FAB_COLORS.floorHighlight),
    });
  } else {
    applyGlossyFloorProps(floorMaterial);
  }
  return floorMaterial;
}

export function getCleanroomSeamMaterial(): THREE.MeshStandardMaterial {
  if (!seamMaterial) {
    seamMaterial = new THREE.MeshStandardMaterial({
      color: CLEANROOM_FLOOR_SEAM,
      metalness: 0,
      roughness: 0.52,
      envMapIntensity: 0.22,
      transparent: true,
      opacity: 0.32,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });
  } else {
    seamMaterial.color.set(CLEANROOM_FLOOR_SEAM);
    seamMaterial.opacity = 0.32;
  }
  return seamMaterial;
}

export function getCleanroomMarkingMaterial(): THREE.MeshPhysicalMaterial {
  if (!markingMaterial) {
    markingMaterial = new THREE.MeshPhysicalMaterial({
      color: CLEANROOM_FLOOR_MARKING,
      metalness: 0,
      roughness: 0.32,
      clearcoat: 0.04,
      clearcoatRoughness: 0.22,
      envMapIntensity: 0.32,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
  }
  return markingMaterial;
}

export function getGrilleFrameMaterial(): THREE.MeshStandardMaterial {
  if (!grilleFrameMaterial) {
    grilleFrameMaterial = new THREE.MeshStandardMaterial({
      color: GRILLE_FRAME_COLOR,
      metalness: 0.12,
      roughness: 0.36,
      envMapIntensity: 0.55,
      polygonOffset: true,
      polygonOffsetFactor: -3,
      polygonOffsetUnits: -3,
    });
  }
  return grilleFrameMaterial;
}

export function getGrilleSurfaceMaterial(): THREE.MeshStandardMaterial {
  if (!grilleSurfaceMaterial) {
    grilleSurfaceMaterial = new THREE.MeshStandardMaterial({
      color: GRILLE_SURFACE_COLOR,
      metalness: 0.48,
      roughness: 0.44,
      envMapIntensity: 0.42,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -4,
    });
  } else {
    grilleSurfaceMaterial.color.set(GRILLE_SURFACE_COLOR);
  }
  return grilleSurfaceMaterial;
}

export function getGrilleRecessMaterial(): THREE.MeshStandardMaterial {
  if (!grilleRecessMaterial) {
    grilleRecessMaterial = new THREE.MeshStandardMaterial({
      color: GRILLE_RECESS_COLOR,
      metalness: 0.35,
      roughness: 0.52,
      envMapIntensity: 0.18,
      polygonOffset: true,
      polygonOffsetFactor: -5,
      polygonOffsetUnits: -5,
    });
  }
  return grilleRecessMaterial;
}

export function getGrilleHoleMaterial(): THREE.MeshStandardMaterial {
  if (!grilleHoleMaterial) {
    grilleHoleMaterial = new THREE.MeshStandardMaterial({
      color: GRILLE_HOLE_COLOR,
      metalness: 0.55,
      roughness: 0.65,
      envMapIntensity: 0.08,
      polygonOffset: true,
      polygonOffsetFactor: -6,
      polygonOffsetUnits: -6,
    });
  }
  return grilleHoleMaterial;
}

export const getCleanroomJointMaterial = getCleanroomSeamMaterial;
export const getCleanroomGrilleMaterial = getGrilleSurfaceMaterial;
export const getCleanroomGrilleHoleMaterial = getGrilleHoleMaterial;
export const getCleanroomGrilleSlatMaterial = getGrilleHoleMaterial;

function pushSegment(
  out: FloorSegment[],
  cx: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number
) {
  if (sx < 0.01 || sz < 0.01) return;
  out.push({ cx, cz, sx, sy, sz });
}

export function resolveCleanroomFloorDetail(
  width: number,
  depth: number,
  cameraDistance?: number
): CleanroomFloorDetail {
  const span = Math.max(width, depth);
  if (cameraDistance !== undefined) {
    if (cameraDistance > span * 0.45) return 'low';
    if (cameraDistance > span * 0.18) return 'medium';
    return 'high';
  }
  return span > 6000 ? 'medium' : 'high';
}

export function buildFloorSeamSegments(_width: number, _depth: number): FloorSegment[] {
  return [];
}

export function buildGrilleStrips(width: number, depth: number): GrilleStrip[] {
  const s = floorDecorScale(width, depth);
  const strips: GrilleStrip[] = [];
  const halfD = depth / 2 - s.inset;
  const stripLen = width * s.grilleLenRatio;
  const count = Math.max(3, Math.min(6, Math.floor((depth - s.inset * 2) / s.grilleSpacing)));
  for (let i = 0; i < count; i++) {
    const cz = -halfD + s.grilleSpacing * (i + 0.62);
    strips.push({ cx: 0, cz, length: stripLen, width: s.grilleWidth });
  }
  return strips;
}

/** Lane markings — omitted on tile floor (grille bands removed). */
export function buildFloorMarkingSegments(_width: number, _depth: number): FloorSegment[] {
  return [];
}

function buildGrillePerforations(
  strip: GrilleStrip,
  s: ReturnType<typeof floorDecorScale>,
  detail: CleanroomFloorDetail,
  out: FloorSegment[]
) {
  if (detail === 'low') return;
  const cols = Math.floor(strip.length / s.holePitch);
  const rows = Math.max(3, Math.floor(strip.width / s.holePitch));
  const maxCols = detail === 'high' ? cols : Math.min(cols, 64);
  const maxRows = detail === 'high' ? rows : Math.min(rows, 5);
  const holeH = s.recessH * 0.4;
  for (let r = 0; r < maxRows; r++) {
    for (let c = 0; c < maxCols; c++) {
      const tx = -strip.length * 0.46 + (c + 0.5) * (strip.length * 0.92 / maxCols);
      const tz = -strip.width * 0.42 + (r + 0.5) * (strip.width * 0.84 / maxRows);
      pushSegment(out, strip.cx + tx, strip.cz + tz, s.holeD, holeH, s.holeD);
    }
  }
}

export function buildFloorGrilleSegments(
  width: number,
  depth: number,
  detail: CleanroomFloorDetail
): Pick<
  CleanroomFloorLayout,
  'grilleStrips' | 'grilleFrames' | 'grilleRecesses' | 'grilleSurfaces' | 'grilleHoles'
> {
  const s = floorDecorScale(width, depth);
  const strips = buildGrilleStrips(width, depth);
  const frames: FloorSegment[] = [];
  const recesses: FloorSegment[] = [];
  const surfaces: FloorSegment[] = [];
  const holes: FloorSegment[] = [];

  for (const strip of strips) {
    pushSegment(recesses, strip.cx, strip.cz, strip.length * 0.98, s.recessH, strip.width * 0.94);
    pushSegment(frames, strip.cx, strip.cz - strip.width / 2, strip.length, s.borderT, s.borderT);
    pushSegment(frames, strip.cx, strip.cz + strip.width / 2, strip.length, s.borderT, s.borderT);
    pushSegment(
      frames,
      strip.cx,
      strip.cz,
      strip.length,
      s.frameT,
      strip.width + s.frameT * 0.6
    );
    pushSegment(surfaces, strip.cx, strip.cz, strip.length * 0.94, s.frameT * 0.45, strip.width * 0.88);
    if (detail !== 'low') buildGrillePerforations(strip, s, detail, holes);
  }

  return { grilleStrips: strips, grilleFrames: frames, grilleRecesses: recesses, grilleSurfaces: surfaces, grilleHoles: holes };
}

export function buildCleanroomFloorLayout(
  width: number,
  depth: number,
  _detail: CleanroomFloorDetail = 'high'
): CleanroomFloorLayout {
  void _detail;
  return {
    seams: buildFloorSeamSegments(width, depth),
    markings: buildFloorMarkingSegments(width, depth),
    grilleStrips: [],
    grilleFrames: [],
    grilleRecesses: [],
    grilleSurfaces: [],
    grilleHoles: [],
  };
}

export const buildCleanroomJointSegments = buildFloorSeamSegments;
export const buildCleanroomMarkingSegments = buildFloorMarkingSegments;
export function buildCleanroomGrilleSegments(width: number, depth: number): FloorSegment[] {
  return buildFloorGrilleSegments(width, depth, 'medium').grilleSurfaces;
}
export function buildCleanroomGrilleHoleSegments(grilles: FloorSegment[]): FloorSegment[] {
  void grilles;
  return [];
}
export const buildCleanroomGrilleSlatSegments = buildCleanroomGrilleHoleSegments;

export function segmentsToInstanceMatrices(
  segments: FloorSegment[],
  yOffset: number,
  options?: { flatCylinder?: boolean }
): Float32Array {
  const dummy = new THREE.Object3D();
  const matrices = new Float32Array(segments.length * 16);
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    dummy.position.set(seg.cx, yOffset, seg.cz);
    if (options?.flatCylinder) {
      dummy.rotation.set(Math.PI / 2, 0, 0);
    } else {
      dummy.rotation.set(0, 0, 0);
    }
    dummy.scale.set(seg.sx, seg.sy, seg.sz);
    dummy.updateMatrix();
    dummy.matrix.toArray(matrices, i * 16);
  }
  return matrices;
}

export function syncInstancedMatrices(mesh: THREE.InstancedMesh | null, matrices: Float32Array): void {
  if (!mesh || matrices.length === 0) return;
  mesh.instanceMatrix.array.set(matrices);
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
}
